import type { Project, ScopeTarget } from "../api";
import {
  CHECKPOINTS,
  DEMO_HOST,
  DEMO_IDS,
  DEMO_PIPELINE,
  DEMO_VERIFY_TOKEN,
  eventsAtCheckpoint,
} from "./demoData";
import type { Checkpoint, DemoScan, DemoState, ScheduledEvent } from "./demoTypes";

/** How fast the current checkpoint's events trickle into the feed, so the
 *  Live feed and graph animate rather than popping in at once. */
export const REVEAL_TICK_MS = 320;

export interface Transition {
  state: DemoState;
  schedule: ScheduledEvent[];
}

export const noChange = (state: DemoState): Transition => ({ state, schedule: [] });

const iso = (ms: number) => new Date(ms).toISOString();

// ---- Project / target ---------------------------------------------------------

function cleanHost(raw: string): string {
  return raw.trim().replace(/^https?:\/\//i, "").replace(/\/.*$/, "").toLowerCase();
}

/** POST /projects — the demo's single project, named after the host. */
export function createProject(
  state: DemoState,
  body: { name?: string; description?: string; jurisdiction_code?: string },
  now: number,
): Transition {
  const host = cleanHost(body.name || body.description || DEMO_HOST) || DEMO_HOST;
  const project: Project = {
    project_id: DEMO_IDS.project,
    name: host,
    description: host,
    created_by: "demo-user",
    created_at: iso(now),
    updated_at: iso(now),
    total_scope_targets: 1,
    verified_targets: 0,
    jurisdiction_code: body.jurisdiction_code,
  };
  return noChange({ ...state, host, project });
}

/** POST /projects/:id/scope/targets — the pending target for the host. */
export function addTarget(state: DemoState, targetHost: string, now: number): Transition {
  const host = cleanHost(targetHost) || state.host || DEMO_HOST;
  const target: ScopeTarget = {
    target_id: DEMO_IDS.target,
    project_id: DEMO_IDS.project,
    target: host,
    kind: "domain",
    attestation: null,
    verification_status: "pending",
    verification_method: null,
    verification_token: null,
    verification_instruction: null,
    verification_started_at: null,
    verified_at: null,
    verification_expires_at: null,
    failure_reason: null,
    created_at: iso(now),
  };
  return noChange({ ...state, host, target });
}

/** POST /scope/targets/:id/verification/start — issue the TXT record. */
export function startVerify(state: DemoState, now: number): Transition {
  if (!state.target) return noChange(state);
  const target: ScopeTarget = {
    ...state.target,
    verification_status: "pending",
    verification_method: "dns",
    verification_token: DEMO_VERIFY_TOKEN,
    verification_instruction: JSON.stringify({
      type: "TXT",
      name: "@",
      value: DEMO_VERIFY_TOKEN,
      ttl: 3600,
    }),
    verification_started_at: iso(now),
  };
  return noChange({ ...state, target });
}

/** POST /scope/targets/:id/verification/complete — always verifies in the demo. */
export function completeVerify(state: DemoState, now: number): Transition {
  if (!state.target || !state.project) return noChange(state);
  const target: ScopeTarget = {
    ...state.target,
    verification_status: "verified",
    verified_at: iso(now),
  };
  const project: Project = { ...state.project, verified_targets: 1 };
  return noChange({ ...state, target, project });
}

// ---- Scan ---------------------------------------------------------------------

/** How far back `started_at` sits so the timer reads the checkpoint's real
 *  elapsed minutes. */
function startedAtFor(k: Checkpoint, now: number): string {
  const mins = CHECKPOINTS[k].elapsedMin;
  return iso(now - mins * 60_000);
}

function revealSchedule(k: Checkpoint, now: number): ScheduledEvent[] {
  const total = eventsAtCheckpoint(k);
  const out: ScheduledEvent[] = [];
  for (let i = 1; i <= total; i++) {
    out.push({ at: now + i * REVEAL_TICK_MS, event: { type: "reveal-tick" } });
  }
  return out;
}

/** POST /scans — start the demo scan at RECON. */
export function startScan(state: DemoState, now: number): Transition {
  const scan: DemoScan = {
    scan_id: DEMO_IDS.scan,
    status: "running",
    checkpoint: 0,
    started_at: startedAtFor(0, now),
    completed_at: null,
    revealed: 0,
    paused_at: null,
  };
  return { state: { ...state, scan }, schedule: revealSchedule(0, now) };
}

/** Move the scan to the next checkpoint (the tour's Next during the scan). */
export function advanceCheckpoint(state: DemoState, now: number): Transition {
  if (!state.scan) return noChange(state);
  const next = Math.min(5, state.scan.checkpoint + 1) as Checkpoint;
  if (next === state.scan.checkpoint) return noChange(state);
  const completed = next === 5;
  const scan: DemoScan = {
    ...state.scan,
    checkpoint: next,
    status: completed ? "completed" : "running",
    // On completion freeze the timer: pin started_at to the final offset and
    // set completed_at so the elapsed total reads ~03:06 instead of ticking on.
    started_at: startedAtFor(next, now),
    completed_at: completed ? iso(now) : null,
    revealed: 0,
    paused_at: null,
  };
  return { state: { ...state, scan }, schedule: revealSchedule(next, now) };
}

/** One reveal tick: expose the next event of the current checkpoint. */
export function revealTick(state: DemoState): Transition {
  if (!state.scan) return noChange(state);
  const total = eventsAtCheckpoint(state.scan.checkpoint);
  if (state.scan.revealed >= total) return noChange(state);
  return noChange({ ...state, scan: { ...state.scan, revealed: state.scan.revealed + 1 } });
}

export function pauseScan(state: DemoState, now: number): Transition {
  if (!state.scan || state.scan.status !== "running") return noChange(state);
  return noChange({ ...state, scan: { ...state.scan, status: "paused", paused_at: iso(now) } });
}

export function resumeScan(state: DemoState, now: number): Transition {
  if (!state.scan || state.scan.status !== "paused") return noChange(state);
  // Credit the paused span back into started_at so the timer picks up where it
  // left off instead of jumping.
  const pausedMs = state.scan.paused_at ? now - new Date(state.scan.paused_at).getTime() : 0;
  const started = new Date(state.scan.started_at).getTime() + Math.max(0, pausedMs);
  return noChange({ ...state, scan: { ...state.scan, status: "running", paused_at: null, started_at: iso(started) } });
}

/** POST /scans/:id/cancel — drop the scan; chapter 3 (Start) runs again. */
export function cancelScan(state: DemoState): Transition {
  return noChange({ ...state, scan: null });
}

export function markNotificationRead(state: DemoState, id: string): Transition {
  if (state.readNotificationIds.includes(id)) return noChange(state);
  return noChange({ ...state, readNotificationIds: [...state.readNotificationIds, id] });
}

export { DEMO_PIPELINE };
